import {
  DEFAULT_MAX_ATTACHMENT_FILE_SIZE_HN_SKJEMA_BYTES,
  DEFAULT_MAX_ATTACHMENT_SINGLE_FILE_FILE_SIZE_HN_SKJEMA_BYTES,
  MAX_ATTACHMENT_FILE_SIZE_HN_SKJEMA_MB,
  NUMBER_OF_ALLOWED_ATTACHMENTS,
  VALID_FILE_TYPES,
} from 'src/constants/constants';

import type {
  Attachment,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from 'fhir/r4';
import type { Metadata } from 'src/types/MinHelseEntities';
import type { HNSkjemaFrontend } from 'src/types/Resources';

import { getMaxOccursExtensionValue, getMaxSizeExtensionValue, getMinOccursExtensionValue } from '@helsenorge/refero';

export function vedleggMaFjernes(metadata: Metadata, beholdVedleggOverstyring?: boolean): boolean {
  if (beholdVedleggOverstyring == true) {
    return false;
  }

  return metadata.HarVedlegg && metadata.IsNewDocument && metadata.HarInnbyggerResponse;
}
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export const createUrlLink = (att: Attachment): string => {
  if (!att.data) {
    return '';
  }
  if (!att.contentType) {
    return '';
  }
  const blob = new Blob([base64ToArrayBuffer(att.data)], { type: att.contentType });

  return URL.createObjectURL(blob);
};

export function openAttachmentInBrowser(att: Attachment): void {
  const url = createUrlLink(att);

  window.open(url, '_blank');

  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

function getQuestionnaireItemByLinkId(items: QuestionnaireItem[] | undefined, linkId?: string): QuestionnaireItem | undefined {
  if (!items || !linkId) {
    return undefined;
  }
  for (let i = 0; i < items.length; i++) {
    if (items[i].linkId === linkId) {
      return items[i];
    } else if (items[i].item) {
      const foundItem = getQuestionnaireItemByLinkId(items[i].item, linkId);
      if (foundItem) {
        return foundItem;
      }
    }
  }
  return undefined;
}

export function maxFileSizeForLinkIdInBytes(questionnaire: Questionnaire | null | undefined, linkId: string | undefined): number {
  if (!questionnaire || !linkId) return DEFAULT_MAX_ATTACHMENT_SINGLE_FILE_FILE_SIZE_HN_SKJEMA_BYTES;

  const qItem = getQuestionnaireItemByLinkId(questionnaire.item, linkId);
  const maxSize = getMaxSizeExtensionValue(qItem);
  if (maxSize) {
    const maxSizeInBytes = maxSize * 1024 * 1024;
    if (maxSizeInBytes < DEFAULT_MAX_ATTACHMENT_SINGLE_FILE_FILE_SIZE_HN_SKJEMA_BYTES) {
      return maxSizeInBytes;
    }
  }
  return DEFAULT_MAX_ATTACHMENT_SINGLE_FILE_FILE_SIZE_HN_SKJEMA_BYTES;
}
export function maxOccurrencesForLinkId(questionnaire: Questionnaire | null | undefined, linkId: string | undefined): number {
  if (!questionnaire || !linkId) return DEFAULT_MAX_ATTACHMENT_SINGLE_FILE_FILE_SIZE_HN_SKJEMA_BYTES;

  const qItem = getQuestionnaireItemByLinkId(questionnaire.item, linkId);
  const maxOccurrences = getMaxOccursExtensionValue(qItem);
  return maxOccurrences && maxOccurrences < NUMBER_OF_ALLOWED_ATTACHMENTS ? maxOccurrences : NUMBER_OF_ALLOWED_ATTACHMENTS;
}
export function minOccurrencesForLinkId(questionnaire: Questionnaire | null | undefined, linkId: string | undefined): number {
  if (!questionnaire || !linkId) return 0;

  const qItem = getQuestionnaireItemByLinkId(questionnaire.item, linkId);
  const minOccurrences = getMinOccursExtensionValue(qItem);
  return minOccurrences || 0;
}
function getTotalNumberOfAttachments(questionnaireResponse: QuestionnaireResponse): number {
  const allAttachments: AttachmentAnswersWithLinkId[] = [];

  if (questionnaireResponse.item) {
    for (let i = 0; i < questionnaireResponse.item.length; i++) {
      allAttachments.push(...collectAttachmentAnswers(questionnaireResponse.item[i]));
    }
  }
  const numberOfAttachments = allAttachments.length;
  return numberOfAttachments;
}

export function calculateBase64SizeInBytes(base64Data: string): number {
  // Remove any whitespace in the base64 string
  const cleanedBase64 = base64Data.replace(/\s/g, '');
  // Determine the number of padding characters at the end of the string.
  let padding = 0;
  if (cleanedBase64.endsWith('==')) {
    padding = 2;
  } else if (cleanedBase64.endsWith('=')) {
    padding = 1;
  }
  // Calculate and return the size using the formula:
  // decodedSize = (base64Length * 3/4) - padding
  return (cleanedBase64.length * 3) / 4 - padding;
}
export type AttachmentAnswersWithLinkId = {
  linkId: string | undefined;
  answer: QuestionnaireResponseItemAnswer;
};
export type AttachmentAnswersByLinkIdGrouped = {
  [linkId: string]: AttachmentAnswersWithLinkId[];
};
export function collectAttachmentAnswers(qrItem: QuestionnaireResponseItem): AttachmentAnswersWithLinkId[] {
  const attachments: AttachmentAnswersWithLinkId[] = [];

  if (qrItem.answer) {
    for (let i = 0; i < qrItem.answer.length; i++) {
      const answer = qrItem.answer[i];
      if (answer.valueAttachment) {
        attachments.push({ answer, linkId: qrItem?.linkId });
      }
      if (answer.item) {
        for (let j = 0; j < answer.item.length; j++) {
          attachments.push(...collectAttachmentAnswers(answer.item[j]));
        }
      }
    }
  }

  if (qrItem.item) {
    for (let i = 0; i < qrItem.item.length; i++) {
      attachments.push(...collectAttachmentAnswers(qrItem.item[i]));
    }
  }
  return attachments;
}

export function getAllAttachmentAnswers(questionnaireResponse: QuestionnaireResponse): AttachmentAnswersWithLinkId[] {
  const allAttachments: AttachmentAnswersWithLinkId[] = [];

  if (questionnaireResponse.item) {
    for (let i = 0; i < questionnaireResponse.item.length; i++) {
      allAttachments.push(...collectAttachmentAnswers(questionnaireResponse.item[i]));
    }
  }

  return allAttachments;
}
export function hasTooManyAttachments(
  questionnaireResponse: QuestionnaireResponse | null | undefined,
  resources: HNSkjemaFrontend | null
): false | string[] {
  if (!questionnaireResponse || !resources) {
    return false;
  }
  if (getTotalNumberOfAttachments(questionnaireResponse) > NUMBER_OF_ALLOWED_ATTACHMENTS) {
    return [`${resources?.attachment_max_occurrences?.replace('{0}', NUMBER_OF_ALLOWED_ATTACHMENTS.toString())}`];
  }
  return false;
}
const allAttachmentsGroupByLinkId = (questionnaireResponse: QuestionnaireResponse): AttachmentAnswersByLinkIdGrouped =>
  getAllAttachmentAnswers(questionnaireResponse).reduce((acc: AttachmentAnswersByLinkIdGrouped, curr) => {
    if (!curr.linkId) {
      return acc;
    }
    if (!acc[curr.linkId]) {
      acc[curr.linkId] = [];
    }
    acc[curr.linkId].push(curr);
    return acc;
  }, {});
export function hasTooManyAttachmentsForSingleItem(
  questionnaireResponse: QuestionnaireResponse | null | undefined,
  questionnaire: Questionnaire | null | undefined,
  resources: HNSkjemaFrontend | null
): false | string[] {
  if (!questionnaireResponse || !questionnaire) {
    return false;
  }
  const errors: string[] = [];
  const allAttachmentsGrouped = allAttachmentsGroupByLinkId(questionnaireResponse);
  for (const linkId in allAttachmentsGrouped) {
    const maxAllowed = maxOccurrencesForLinkId(questionnaire, linkId);
    const minAllowed = minOccurrencesForLinkId(questionnaire, linkId);
    const attachmentsForThisLinkId = allAttachmentsGrouped[linkId];
    if (maxAllowed < attachmentsForThisLinkId.length) {
      const itemText = getQuestionnaireItemByLinkId(questionnaire.item, linkId)?.text;
      const display = itemText ? `- ${itemText}` : linkId;
      errors.push(
        `${resources?.attachment_max_occurrences_single_file?.replace('{0}', maxAllowed.toString()).replace('{1}', display || '')}`
      );
    } else if (minAllowed > attachmentsForThisLinkId.length) {
      const itemText = getQuestionnaireItemByLinkId(questionnaire.item, linkId)?.text;
      const display = itemText ? `- ${itemText}` : linkId;
      errors.push(
        `${resources?.attachment_min_occurrences_single_file?.replace('{0}', minAllowed.toString()).replace('{1}', display || '')}`
      );
    }
  }
  return errors.length > 0 ? errors : false;
}
export function getAttachmentAnswerSizeInBytes(answer: QuestionnaireResponseItemAnswer): number | undefined {
  if (!answer.valueAttachment || !answer.valueAttachment.data) {
    return undefined;
  }

  const { data } = answer.valueAttachment;

  return calculateBase64SizeInBytes(data);
}

export function attachmentsOverSizeLimit(
  questionnaireResponse: QuestionnaireResponse | null | undefined,
  resources: HNSkjemaFrontend | null
): false | string[] {
  if (!questionnaireResponse || !resources) {
    return false;
  }

  const attachments = getAllAttachmentAnswers(questionnaireResponse);

  const totalAttachmentSizeInBytes = attachments.reduce((acc, attachment) => {
    const size = getAttachmentAnswerSizeInBytes(attachment.answer) || 0;
    return acc + size;
  }, 0);
  if (totalAttachmentSizeInBytes > DEFAULT_MAX_ATTACHMENT_FILE_SIZE_HN_SKJEMA_BYTES) {
    return [resources?.attachmentError_fileSize_total?.replace('{0}', MAX_ATTACHMENT_FILE_SIZE_HN_SKJEMA_MB.toString())];
  }
  return false;
}

export function attachmentOneOfFilesOverSizeLimit(
  questionnaireResponse: QuestionnaireResponse | null | undefined,
  questionnaire: Questionnaire | null | undefined,
  resources: HNSkjemaFrontend | null
): false | string[] {
  if (!questionnaireResponse || !questionnaire || !resources) {
    return false;
  }
  const errors: string[] = [];
  const attachments = getAllAttachmentAnswers(questionnaireResponse);

  for (let i = 0; i < attachments.length; i++) {
    const size = getAttachmentAnswerSizeInBytes(attachments[i].answer) || 0;
    const maxSizeForItem = maxFileSizeForLinkIdInBytes(questionnaire, attachments[i]?.linkId);
    if (size > maxSizeForItem) {
      const display = getQuestionnaireItemByLinkId(questionnaire.item, attachments[i].linkId!)?.text;
      errors.push(`${resources?.attachmentError_fileSize?.replace('{0}', (maxSizeForItem / 1024 / 1024).toString())} - ${display ?? ''}`);
    }
  }

  return errors.length > 0 ? errors : false;
}
export function attachementHasWrongFileTypes(
  questionnaireResponse: QuestionnaireResponse | null | undefined,
  resources: HNSkjemaFrontend | null
): false | string[] {
  if (!questionnaireResponse || !resources) {
    return false;
  }
  const attachments = getAllAttachmentAnswers(questionnaireResponse);
  const allowedFileTypes: string[] = VALID_FILE_TYPES;
  for (let i = 0; i < attachments.length; i++) {
    const contentType = attachments[i].answer.valueAttachment?.contentType || '';
    if (!allowedFileTypes.includes(contentType)) {
      return [resources?.attachmentError_wrong_file_type?.replace('{0}', VALID_FILE_TYPES.join(', '))];
    }
  }
  return false;
}
